import {
	Rate,
	RateScores,
	RateRankings,
	TagSystem,
	PrimaryTag,
	SecondaryTag,
	ContextualTag,
	BadgeTag,
	RateAnalysis,
	AnalyzedRate,
	UserShippingPreferences,
	OrderContext,
	RecommendationLevel,
	PriceStatistics,
	SpeedStatistics,
} from "@/types/shipping";

export class ShippingRateAnalyzer {
	private rates: Rate[];
	private userPreferences: UserShippingPreferences;
	private orderContext: OrderContext;
	private priceStats: PriceStatistics;
	private speedStats: SpeedStatistics;

	constructor(rates: Rate[], preferences?: UserShippingPreferences, context?: OrderContext) {
		this.rates = rates;
		this.userPreferences = preferences || this.getDefaultPreferences();
		this.orderContext = context || {};

		// Calculate statistics upfront
		this.priceStats = this.calculatePriceStatistics();
		this.speedStats = this.calculateSpeedStatistics();
	}

	private getDefaultPreferences(): UserShippingPreferences {
		return {
			customerId: "default",
			speedWeight: 0.3,
			priceWeight: 0.4,
			reliabilityWeight: 0.3,
			preferredCarriers: [],
			avoidCarriers: [],
			preferFlatRate: false,
			ecoFriendlyPreferred: false,
		};
	}

	analyzeRates(): AnalyzedRate[] {
		const scores = this.calculateScores();
		const rankings = this.generateRankings(scores);
		const analyzedRates = this.rates.map((rate, index) => ({
			...rate,
			analysis: {
				scores: scores[index],
				rankings: rankings[index],
				tags: this.assignTags(rate, scores[index], rankings[index], index),
				recommendation: this.getRecommendation(scores[index], rankings[index]),
			},
		}));

		return analyzedRates;
	}

	private calculateScores(): RateScores[] {
		return this.rates.map((rate) => {
			const scores: RateScores = {
				price: this.calculatePriceScore(rate),
				speed: this.calculateSpeedScore(rate),
				reliability: this.calculateReliabilityScore(rate),
				value: this.calculateValueScore(rate),
				carrierTrust: this.calculateCarrierTrustScore(rate),
				convenience: this.calculateConvenienceScore(rate),
				overall: 0,
			};

			// Calculate weighted overall score
			scores.overall = this.calculateWeightedScore(scores);
			return scores;
		});
	}

	private calculatePriceScore(rate: Rate): number {
		const price = rate.shippingAmount.amount;
		const { min, max, percentiles } = this.priceStats;

		if (max === min) return 50; // All prices are the same

		// Inverse score - lower price = higher score
		let score = 100 - ((price - min) / (max - min)) * 100;

		// Apply non-linear scaling to reward lower prices more
		if (price <= percentiles.p25) {
			score = 85 + (score / 85) * 15; // Boost scores for bottom quartile
		} else if (price >= percentiles.p75) {
			score = score * 0.7; // Penalize top quartile prices
		}

		// Penalty for avoided carriers
		if (rate.carrierCode && this.userPreferences.avoidCarriers.includes(rate.carrierCode)) {
			score *= 0.5;
		}

		return Math.max(0, Math.min(100, score));
	}

	private calculateSpeedScore(rate: Rate): number {
		if (!rate.deliveryDays) return 50;

		const days = rate.deliveryDays;
		const { min, max } = this.speedStats;

		if (max === min) return 50;

		// Inverse score - fewer days = higher score
		let score = 100 - ((days - min) / (max - min)) * 100;

		// Bonus for specific delivery speeds
		if (days === 1) score = Math.min(100, score + 20); // Next day bonus
		if (days === 2) score = Math.min(95, score + 10); // 2-day bonus

		// Apply user preference for speed
		if (this.userPreferences.preferredDeliveryDays) {
			const diff = Math.abs(days - this.userPreferences.preferredDeliveryDays);
			score -= diff * 10; // Penalty for deviation from preference
		}

		return Math.max(0, Math.min(100, score));
	}

	private calculateReliabilityScore(rate: Rate): number {
		let score = 50; // Base score

		// Guaranteed service is highly reliable
		if (rate.guaranteedService) score += 30;

		// Trackable shipments are more reliable
		if (rate.trackable) score += 20;

		// Preferred carriers get a boost
		if (rate.carrierCode && this.userPreferences.preferredCarriers.includes(rate.carrierCode)) {
			score += 15;
		}

		// Major carriers are generally more reliable
		const majorCarriers = ["ups", "fedex", "usps"];
		if (rate.carrierCode && majorCarriers.includes(rate.carrierCode.toLowerCase())) {
			score += 10;
		}

		// Negotiated rates suggest established relationship
		if (rate.negotiatedRate) score += 5;

		return Math.min(100, score);
	}

	private calculateValueScore(rate: Rate): number {
		const priceScore = this.calculatePriceScore(rate);
		const speedScore = this.calculateSpeedScore(rate);
		const reliabilityScore = this.calculateReliabilityScore(rate);

		// Value is a combination of all factors
		const baseValue = priceScore * 0.4 + speedScore * 0.4 + reliabilityScore * 0.2;

		// Boost for sweet spot combinations
		if (priceScore > 70 && speedScore > 60) {
			return Math.min(100, baseValue + 10);
		}

		return baseValue;
	}

	private calculateCarrierTrustScore(rate: Rate): number {
		let score = 70; // Base trust score

		const trustedCarriers = {
			ups: 95,
			fedex: 95,
			usps: 90,
			fedex_walleted: 93,
		};

		if (rate.carrierCode) {
			const carrierCode = rate.carrierCode.toLowerCase();
			if (carrierCode in trustedCarriers) {
				score = trustedCarriers[carrierCode as keyof typeof trustedCarriers];
			}
		}

		// User preferences override
		if (rate.carrierCode && this.userPreferences.preferredCarriers.includes(rate.carrierCode)) {
			score = Math.max(score, 90);
		}
		if (rate.carrierCode && this.userPreferences.avoidCarriers.includes(rate.carrierCode)) {
			score = Math.min(score, 30);
		}

		return score;
	}

	private calculateConvenienceScore(rate: Rate): number {
		let score = 50;

		// Flat rate packages are convenient
		if (rate.packageType?.includes("flat_rate")) score += 20;

		// Ground shipping to nearby zones is convenient
		if (rate.zone && rate.zone <= 2 && rate.serviceCode?.includes("ground")) {
			score += 15;
		}

		// Services with clear delivery windows are convenient
		if (rate.estimatedDeliveryDate && rate.guaranteedService) {
			score += 15;
		}

		return Math.min(100, score);
	}

	private calculateWeightedScore(scores: RateScores): number {
		const weights = this.userPreferences;

		return (
			scores.price * weights.priceWeight +
			scores.speed * weights.speedWeight +
			scores.reliability * weights.reliabilityWeight +
			scores.value *
				(1 - weights.priceWeight - weights.speedWeight - weights.reliabilityWeight) *
				0.5 +
			scores.carrierTrust * 0.1 +
			scores.convenience * 0.05
		);
	}

	private generateRankings(scores: RateScores[]): RateRankings[] {
		const priceRanks = this.rankByField(scores, "price");
		const speedRanks = this.rankByField(scores, "speed");
		const valueRanks = this.rankByField(scores, "value");
		const overallRanks = this.rankByField(scores, "overall");

		return scores.map((_, index) => ({
			byPrice: priceRanks[index],
			bySpeed: speedRanks[index],
			byValue: valueRanks[index],
			byOverall: overallRanks[index],
		}));
	}

	private rankByField(scores: RateScores[], field: keyof RateScores): number[] {
		const indexed = scores.map((score, index) => ({ score: score[field], index }));
		indexed.sort((a, b) => b.score - a.score); // Higher score = better rank

		const ranks = new Array(scores.length);
		indexed.forEach((item, rank) => {
			ranks[item.index] = rank + 1;
		});

		return ranks;
	}

	private assignTags(
		rate: Rate,
		scores: RateScores,
		rankings: RateRankings,
		index: number
	): TagSystem {
		const tags: TagSystem = {
			primary: null,
			secondary: [],
			contextual: [],
			badges: [],
		};

		// Assign primary tag (only one)
		tags.primary = this.assignPrimaryTag(rate, scores, rankings);

		// Assign secondary tags
		tags.secondary = this.assignSecondaryTags(rate, scores, rankings);

		// Assign contextual tags
		tags.contextual = this.assignContextualTags(rate);

		// Assign badges
		tags.badges = this.assignBadges(rate, scores);

		return tags;
	}

	private assignPrimaryTag(
		rate: Rate,
		scores: RateScores,
		rankings: RateRankings
	): PrimaryTag | null {
		// Only assign primary tags to standout options
		if (this.rates.length < 3) return null;

		// Best overall - must be top ranked and high score
		if (rankings.byOverall === 1 && scores.overall >= 80) {
			return PrimaryTag.BEST_OVERALL;
		}

		// Cheapest - must be significantly cheaper
		if (rankings.byPrice === 1 && this.isPriceDifferenceSignificant()) {
			return PrimaryTag.CHEAPEST;
		}

		// Fastest - must be significantly faster
		if (rankings.bySpeed === 1 && this.isSpeedDifferenceSignificant()) {
			return PrimaryTag.FASTEST;
		}

		// Recommended - high score but not the best
		if (rankings.byOverall <= 3 && scores.overall >= 75) {
			return PrimaryTag.RECOMMENDED;
		}

		return null;
	}

	private assignSecondaryTags(
		rate: Rate,
		scores: RateScores,
		rankings: RateRankings
	): SecondaryTag[] {
		const tags: SecondaryTag[] = [];

		// Great Value - good balance of price and features
		if (rankings.byValue <= 2 && scores.value >= 75) {
			tags.push(SecondaryTag.GREAT_VALUE);
		}

		// Service level tags
		const serviceType = (rate.serviceType || rate.serviceCode || "").toLowerCase();

		if (
			rate.deliveryDays === 1 ||
			serviceType.includes("overnight") ||
			serviceType.includes("express")
		) {
			tags.push(SecondaryTag.EXPRESS);
		} else if (
			(rate.deliveryDays !== undefined && rate.deliveryDays >= 5) ||
			serviceType.includes("economy") ||
			serviceType.includes("saver")
		) {
			tags.push(SecondaryTag.ECONOMY);
		} else if (serviceType.includes("priority") || serviceType.includes("2day")) {
			tags.push(SecondaryTag.PREMIUM);
		} else if (serviceType.includes("ground") && rate.deliveryDays !== undefined && rate.deliveryDays <= 3) {
			tags.push(SecondaryTag.STANDARD);
		}

		return tags;
	}

	private assignContextualTags(rate: Rate): ContextualTag[] {
		const tags: ContextualTag[] = [];

		// Delivery time tags
		if (rate.deliveryDays === 0) {
			tags.push(ContextualTag.SAME_DAY);
		} else if (rate.deliveryDays === 1) {
			tags.push(ContextualTag.NEXT_DAY);

			// Check if it arrives tomorrow
			if (rate.estimatedDeliveryDate) {
				const tomorrow = new Date();
				tomorrow.setDate(tomorrow.getDate() + 1);
				const deliveryDate = new Date(rate.estimatedDeliveryDate);

				if (deliveryDate.toDateString() === tomorrow.toDateString()) {
					tags.push(ContextualTag.ARRIVES_TOMORROW);
				}
			}
		} else if (rate.deliveryDays === 2) {
			tags.push(ContextualTag.TWO_DAY);
		}

		// Weekend delivery check
		if (rate.estimatedDeliveryDate) {
			const deliveryDate = new Date(rate.estimatedDeliveryDate);
			if (deliveryDate.getDay() === 0 || deliveryDate.getDay() === 6) {
				tags.push(ContextualTag.WEEKEND_DELIVERY);
			}
		}

		// Service features
		if (rate.guaranteedService) {
			tags.push(ContextualTag.GUARANTEED_TIME);
		}

		if (rate.trackable) {
			tags.push(ContextualTag.FREE_TRACKING);
		}

		// Signature services
		const serviceType = (rate.serviceType || "").toLowerCase();
		if (serviceType.includes("signature") || (rate.confirmationAmount?.amount && rate.confirmationAmount.amount > 0)) {
			tags.push(ContextualTag.SIGNATURE_AVAILABLE);
		}

		return tags;
	}

	private assignBadges(rate: Rate, scores: RateScores): BadgeTag[] {
		const badges: BadgeTag[] = [];

		// Negotiated rate
		if (rate.negotiatedRate) {
			badges.push(BadgeTag.NEGOTIATED_RATE);
		}

		// Flat rate
		if (rate.packageType?.includes("flat_rate")) {
			badges.push(BadgeTag.FLAT_RATE);
		}

		// Trusted carrier
		if (scores.carrierTrust >= 90) {
			badges.push(BadgeTag.TRUSTED_CARRIER);
		}

		// Most popular (UPS/FedEx Ground, USPS Priority)
		const popularServices = [
			{ carrier: "ups", service: "ground" },
			{ carrier: "fedex", service: "ground" },
			{ carrier: "usps", service: "priority mail" },
		];

		const isPopular = rate.carrierCode && popularServices.some(
			(ps) =>
				rate.carrierCode!.toLowerCase().includes(ps.carrier) &&
				(rate.serviceType?.toLowerCase().includes(ps.service) ||
					rate.serviceCode?.toLowerCase().includes(ps.service))
		);

		if (isPopular && this.rates.length >= 5) {
			badges.push(BadgeTag.MOST_POPULAR);
		}

		// Transportation mode
		const serviceCode = (rate.serviceCode || "").toLowerCase();
		if (serviceCode.includes("ground")) {
			badges.push(BadgeTag.GROUND_SHIPPING);
		} else if (serviceCode.includes("air") || serviceCode.includes("express")) {
			badges.push(BadgeTag.AIR_SHIPPING);
		}

		// Eco-friendly (ground shipping to nearby zones)
		if (rate.zone && rate.zone <= 2 && serviceCode.includes("ground")) {
			badges.push(BadgeTag.ECO_FRIENDLY);
		}

		// Local carrier
		if (rate.zone === 1) {
			badges.push(BadgeTag.LOCAL_CARRIER);
		}

		return badges;
	}

	private getRecommendation(scores: RateScores, rankings: RateRankings): RecommendationLevel {
		if (scores.overall >= 85 && rankings.byOverall <= 2) {
			return RecommendationLevel.HIGHLY_RECOMMENDED;
		} else if (scores.overall >= 70 && rankings.byOverall <= 5) {
			return RecommendationLevel.RECOMMENDED;
		} else if (scores.overall >= 50) {
			return RecommendationLevel.ACCEPTABLE;
		} else {
			return RecommendationLevel.NOT_RECOMMENDED;
		}
	}

	private calculatePriceStatistics(): PriceStatistics {
		const prices = this.rates.map((r) => r.shippingAmount.amount).sort((a, b) => a - b);

		if (prices.length === 0) {
			return {
				min: 0,
				max: 0,
				mean: 0,
				median: 0,
				stdDev: 0,
				percentiles: { p25: 0, p50: 0, p75: 0 },
			};
		}

		const sum = prices.reduce((a, b) => a + b, 0);
		const mean = sum / prices.length;

		const squaredDiffs = prices.map((p) => Math.pow(p - mean, 2));
		const variance = squaredDiffs.reduce((a, b) => a + b, 0) / prices.length;
		const stdDev = Math.sqrt(variance);

		return {
			min: prices[0],
			max: prices[prices.length - 1],
			mean,
			median: prices[Math.floor(prices.length / 2)],
			stdDev,
			percentiles: {
				p25: prices[Math.floor(prices.length * 0.25)],
				p50: prices[Math.floor(prices.length * 0.5)],
				p75: prices[Math.floor(prices.length * 0.75)],
			},
		};
	}

	private calculateSpeedStatistics(): SpeedStatistics {
		const ratesWithDays = this.rates.filter((r) => r.deliveryDays != null);
		const days = ratesWithDays.map((r) => r.deliveryDays!).sort((a, b) => a - b);

		if (days.length === 0) {
			return {
				min: 1,
				max: 7,
				mean: 3,
				fastestCarriers: [],
			};
		}

		const sum = days.reduce((a, b) => a + b, 0);
		const mean = sum / days.length;

		// Find carriers offering the fastest delivery
		const minDays = days[0];
		const fastestCarriers = [
			...new Set(
				ratesWithDays.filter((r) => r.deliveryDays === minDays).map((r) => r.carrierFriendlyName)
			),
		];

		return {
			min: days[0],
			max: days[days.length - 1],
			mean,
			fastestCarriers,
		};
	}

	private isPriceDifferenceSignificant(): boolean {
		const { min, max, stdDev } = this.priceStats;
		const range = max - min;

		// Significant if range is > $2 and std dev is meaningful
		return range > 2 && stdDev > 0.5;
	}

	private isSpeedDifferenceSignificant(): boolean {
		const { min, max } = this.speedStats;
		const range = max - min;

		// Significant if there's at least 2 days difference
		return range >= 2;
	}
}
